import { Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const PRACTICES = [
  "Structured validation workflows",
  "Quality-control procedures",
  "Controlled data handling",
  "Confidential treatment of client information",
  "Consistent validation methodology",
  "Client-specific validation requirements",
];

export default function Security() {
  return (
    <section id="security" data-testid="security-section" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel index="05">Data Security &amp; Process</SectionLabel>
              <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-8">
                Built Around Accuracy, Confidentiality &amp; Process Discipline
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-10">
                Every engagement follows a controlled, repeatable validation methodology —
                so results stay consistent across records, portfolios, and teams.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {PRACTICES.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-700 font-medium">
                    <span className="mt-0.5 w-5 h-5 bg-accent-soft flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-accent" strokeWidth={3} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="border border-slate-200 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1763973559043-ee687e8df328?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGRlZXAlMjBibHVlfGVufDB8fHx8MTc4NjI5NTgyMnww&ixlib=rb-4.1.0&q=85"
                    alt="Abstract geometric composition representing structured data control"
                    className="w-full h-80 object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-navy text-white p-6 sm:p-8 max-w-xs border border-navy-light shadow-xl">
                  <p className="font-monodata text-[10px] tracking-[0.25em] uppercase text-blue-400 mb-3">Our Commitment</p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Client information is handled confidentially under controlled,
                    documented processes — every record, every engagement.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="legal-disclaimer"
            className="mt-24 border-l-2 border-accent bg-slate-50 p-8 lg:p-10"
            role="note"
          >
            <p className="font-monodata text-[10px] tracking-[0.25em] uppercase text-accent mb-4">Important Disclaimer</p>
            <p className="text-sm text-slate-600 leading-relaxed max-w-4xl">
              Certify IP Global provides intellectual property data validation, information
              verification, and administrative support services. Our services are not a
              substitute for legal advice, legal representation, or attorney review. Patent
              deadlines and legal rights should be independently confirmed by the responsible
              patent attorney, law firm, or authorized IP professional.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
